// CartPage.jsx
import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useParams } from 'react-router-dom';

import PatientInfoStep from './Steps/PatientInfoStep';
import ServiceInfoStep from './Steps/ServiceInfoStep';
import PaymentStep from './Steps/PaymentStep';
import Stepper from '@/v2/components/Stepper';
import SuccessStep from './Steps/SuccessStep';
import ErrorStep from './Steps/ErrorStep';

import { useCartData } from './context/CartDataContext';
import { useRegistration } from './context/RegistrationContext';
import useFetch from 'use-http';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { useUserState } from '@/components/context/UserContext';

import {
  buildUiPayload,
  toRegistrationInput,
} from './utils/registrationMapper';
import RegistrationErrorDialog from './Components/RegistrationErrorDialog';
import {
  useCartDispatch,
  useCartState,
} from '@/components/context/CartContext';
import {
  buildBookingTrackingData,
  buildExponeaBookingPayload,
  clearBookingTrackingData,
  clearExponeaBookingPayload,
  persistBookingTrackingData,
  persistExponeaBookingPayload,
  pushAddUserDataEvent,
  pushBeginCheckoutEvent,
  pushBookingConfirmedEvents,
  pushExponeaBookingItemEvent,
  pushExponeaBookingResultEvent,
  readBookingTrackingData,
  readExponeaBookingPayload,
} from '@/v2/utils/bookingTracking';

const STEBBY_REFERENCE_KEY = 'stebbyReference';
const STEBBY_REGISTRATION_INPUT_KEY = 'stebbyRegistrationInput';

const CartPage = () => {
  const [stepIndex, setStepIndex] = useState(0);
  const dispatch = useCartDispatch();
  const [leftToPaysera, setLeftToPaysera] = useState(true);
  const stebbyCallbackHandledRef = useRef(false);
  const beginCheckoutTrackedRef = useRef(false);

  const { status } = useParams();

  const isSuccessRoute = status === 'success';
  const isErrorRoute = status === 'error';
  const isCancelRoute = status === 'cancel';
  const isStebbySuccessRoute = status === 'stebby-success';
  const isStebbyCancelRoute = status === 'stebby-cancel';

  const GENERIC_ERROR_MSG =
    'Atsiprašome, tačiau jūsų registracija nepavyko dėl nenumatytos klaidos. Bandykite dar kartą.';

  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] =
    useState(GENERIC_ERROR_MSG);

  const { post: postOtherServices } = useFetch('/Services/OtherServices');
  const { get: getQuestions } = useFetch('/Services');

  const {
    post: postRegistration,
    response: registrationResponse,
    loading: isFinalizing,
    error: registrationError,
  } = useFetch('/registrations');

  const {
    post: postStebby,
    get: getStebby,
    response: stebbyResponse,
    loading: isStebbyLoading,
  } = useFetch('/stebby');

  const {
    service,
    services,
    loading,
    setOtherServices,
    setQuestions,
    setQuestionsLoading,
    setQuestionsError,
    selectedTimeSlot,
    timeslotReservedUntil,
    setServices,
  } = useCartData();

  const { userInfo, consultationInfo, paymentInfo } = useRegistration();
  const userState = useUserState();
  const cartState = useCartState();
  const history = useHistory();

  const stepTitles = useMemo(
    () => ['Registracija', 'Paslauga', 'Apmokėjimas'],
    [],
  );

  const nextStep = () => {
    if (stepIndex === 0) {
      pushAddUserDataEvent({ services, selectedTimeSlot });
    }

    setStepIndex((i) => Math.min(i + 1, stepTitles.length));
  };

  const prevStep = () => {
    setStepIndex((i) => {
      if (i === 0) {
        history.goBack();
        return 0;
      }

      return i - 1;
    });
  };

  const isStebbyPayment = (paymentValues, registrationInput) => {
    const paymentType =
      paymentValues?.paymentType ||
      paymentValues?.PaymentType ||
      registrationInput?.paymentType ||
      registrationInput?.PaymentType;

    return String(paymentType || '').toLowerCase() === 'stebby';
  };

  const clearStebbySession = () => {
    sessionStorage.removeItem(STEBBY_REFERENCE_KEY);
    sessionStorage.removeItem(STEBBY_REGISTRATION_INPUT_KEY);
  };

  const handleRegistrationSuccess = useCallback(
    (result, registrationInput) => {
      const payseraUrl = result?.payseraUrl || result?.PayseraUrl;
      const registrationId = result?.registrationId || result?.RegistrationId;
      const orderId = result?.orderId || result?.OrderId;
      const bookingTrackingData = buildBookingTrackingData({
        services,
        selectedTimeSlot,
        registrationId,
        orderId,
        paymentInfo,
      });
      const exponeaPayload = buildExponeaBookingPayload({
        services,
        selectedTimeSlot,
        registrationInput,
        registrationId,
        orderId,
        paymentInfo,
      });

      if (typeof payseraUrl === 'string' && payseraUrl.trim() !== '') {
        persistBookingTrackingData(bookingTrackingData);
        persistExponeaBookingPayload(exponeaPayload);

        dispatch({
          type: 'SET_REGISTRATION_ID',
          registrationId,
          orderType: 'registrationService',
        });

        window.location.href = payseraUrl;
        setLeftToPaysera(false);
        setServices(null);
        return;
      }

      pushBookingConfirmedEvents(bookingTrackingData);
      clearBookingTrackingData();
      pushExponeaBookingResultEvent(exponeaPayload, 'Success');
      clearExponeaBookingPayload();
      window.location.replace('/cart/success');
      setStepIndex(stepTitles.length);
      setTimeout(() => setServices(null), 100);
    },
    [
      dispatch,
      paymentInfo,
      selectedTimeSlot,
      services,
      setServices,
      stepTitles.length,
    ],
  );

  const submitRegistration = useCallback(
    async (registrationInput) => {
      const exponeaPayload = buildExponeaBookingPayload({
        services,
        selectedTimeSlot,
        registrationInput,
        paymentInfo,
      });

      pushExponeaBookingItemEvent(exponeaPayload);

      const result = await postRegistration('', registrationInput);

      if (!registrationResponse.ok) {
        console.error('❌ Backend rejected registration:', result);
        setErrorDialogMessage(result?.message || GENERIC_ERROR_MSG);
        setErrorDialogOpen(true);
        return false;
      }

      handleRegistrationSuccess(result, registrationInput);
      return true;
    },
    [
      handleRegistrationSuccess,
      paymentInfo,
      postRegistration,
      registrationResponse,
      selectedTimeSlot,
      services,
    ],
  );

  const startStebbyAuthorization = useCallback(
    async (registrationInput) => {
      try {
        const result = await postStebby('/identify/request-token');

        if (!stebbyResponse.ok || !result?.redirectUrl || !result?.reference) {
          console.error('❌ Failed to start Stebby authorization:', result);
          setErrorDialogMessage(GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
          return;
        }

        sessionStorage.setItem(STEBBY_REFERENCE_KEY, result.reference);
        sessionStorage.setItem(
          STEBBY_REGISTRATION_INPUT_KEY,
          JSON.stringify(registrationInput),
        );

        window.location.href = result.redirectUrl;
      } catch (err) {
        console.error('❌ Stebby authorization error:', err);
        setErrorDialogMessage(GENERIC_ERROR_MSG);
        setErrorDialogOpen(true);
      }
    },
    [postStebby, stebbyResponse],
  );

  const validateRegistrationServices = (registrationInput) => {
    const invalidServices = registrationInput?.RegistrationServices?.filter(
      (x) => !x?.WorkplaceId || !x?.ServiceId,
    );

    return !invalidServices?.length;
  };

  const handleFinalizeCheckout = useCallback(
    async (paymentFormValues) => {
      const mergedPayment = paymentInfo
        ? { ...paymentInfo, ...paymentFormValues }
        : paymentFormValues;

      if (services && services.length > 1) {
        services.forEach((svc) => {
          svc.isPaidLaboratoryService = selectedTimeSlot.isPaid;
        });
      }

      const uiPayload = buildUiPayload({
        svc: services,
        userInfo,
        consultationInfo,
        paymentMerged: mergedPayment,
        selectedTimeSlot,
        timeslotReservedUntil,
        userState,
        childSelected: !!userInfo?.childSelected,
        selectedChildEspbiEsi: userInfo?.selectedChildEspbiEsi || '',
      });

      const registrationInput = toRegistrationInput(uiPayload);

      if (!validateRegistrationServices(registrationInput)) {
        setErrorDialogMessage(
          'Nepavyko suformuoti registracijos paslaugos duomenų. Bandykite pasirinkti laiką dar kartą.',
        );
        setErrorDialogOpen(true);
        return;
      }

      try {
        if (isStebbyPayment(mergedPayment, registrationInput)) {
          await startStebbyAuthorization(registrationInput);
          return;
        }

        await submitRegistration(registrationInput);
      } catch (err) {
        console.error('❌ Registration error:', err);
        setErrorDialogMessage(GENERIC_ERROR_MSG);
        setErrorDialogOpen(true);
      }
    },
    [
      paymentInfo,
      userInfo,
      consultationInfo,
      selectedTimeSlot,
      timeslotReservedUntil,
      userState,
      services,
      startStebbyAuthorization,
      submitRegistration,
    ],
  );

  useEffect(() => {
    if (!service || loading) return;

    const svc = Array.isArray(service) ? service[0] : service;
    if (!svc?.specialistId) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await postOtherServices('', {
          serviceId: service.serviceId,
          workplaceId: service.workplaceId,
        });

        if (!cancelled) {
          setOtherServices(res);
        }
      } catch (e) {
        console.error('Failed to load other services', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    service,
    loading,
    postOtherServices,
    setOtherServices,
    service?.serviceId,
    service?.workplaceId,
  ]);

  useEffect(() => {
    if (!service || loading) return;

    let cancelled = false;

    (async () => {
      try {
        setQuestionsLoading(true);
        setQuestionsError(null);

        const res = await getQuestions(
          `/${service.workplaceId}/${service.serviceId}/questions`,
        );

        if (!cancelled) {
          setQuestions(res);
        }
      } catch (e) {
        if (!cancelled) {
          setQuestionsError(e?.message || 'Nepavyko gauti klausimų');
        }

        console.error('Failed to load questions', e);
      } finally {
        if (!cancelled) {
          setQuestionsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    service?.serviceId,
    service?.workplaceId,
    loading,
    getQuestions,
    setQuestions,
    setQuestionsError,
    setQuestionsLoading,
  ]);

  useEffect(() => {
    if (isSuccessRoute) {
      const trackingData = readBookingTrackingData();
      pushBookingConfirmedEvents(trackingData);
      clearBookingTrackingData();
      const exponeaPayload = readExponeaBookingPayload();
      pushExponeaBookingResultEvent(exponeaPayload, 'Success');
      clearExponeaBookingPayload();

      dispatch({ type: 'RESET_REGISTRATION_ID' });

      if (userState.isLoggedIn) {
        history.push('/userAccount/visits', { fromPayment: true });
        return;
      }

      setStepIndex(stepTitles.length);
      return;
    }

    if (isErrorRoute) {
      const exponeaPayload = readExponeaBookingPayload();
      pushExponeaBookingResultEvent(exponeaPayload, 'Cancelled');
      clearExponeaBookingPayload();
      setStepIndex(-1);
      return;
    }

    if (isCancelRoute) {
      const exponeaPayload = readExponeaBookingPayload();
      pushExponeaBookingResultEvent(exponeaPayload, 'Cancelled');
      clearExponeaBookingPayload();
      if (cartState.registrationId) {
        history.replace('/payment/status/cancel');
        return;
      }
      setStepIndex(-1);
      return;
    }

    if (isStebbyCancelRoute) {
      const exponeaPayload = readExponeaBookingPayload();
      pushExponeaBookingResultEvent(exponeaPayload, 'Cancelled');
      clearExponeaBookingPayload();
      clearStebbySession();
      setStepIndex(-1);
    }
  }, [
    isSuccessRoute,
    isErrorRoute,
    isCancelRoute,
    isStebbyCancelRoute,
    stepTitles.length,
    userState.isLoggedIn,
    history,
    dispatch,
    cartState?.registrationId,
  ]);

  useEffect(() => {
    if (beginCheckoutTrackedRef.current || loading || !services?.length) {
      return;
    }

    beginCheckoutTrackedRef.current = true;
    pushBeginCheckoutEvent({ services, selectedTimeSlot });
  }, [loading, selectedTimeSlot, services]);

  useEffect(() => {
    if (!isStebbySuccessRoute || stebbyCallbackHandledRef.current) return;

    stebbyCallbackHandledRef.current = true;

    const completeStebbyFlow = async () => {
      try {
        const reference = sessionStorage.getItem(STEBBY_REFERENCE_KEY);
        const registrationInputRaw = sessionStorage.getItem(
          STEBBY_REGISTRATION_INPUT_KEY,
        );

        if (!reference || !registrationInputRaw) {
          console.error('❌ Missing Stebby reference or registration payload');
          clearStebbySession();
          setErrorDialogMessage(GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
          setStepIndex(-1);
          return;
        }

        const tokenResult = await getStebby(
          `/identify/token/${encodeURIComponent(reference)}`,
        );

        if (!stebbyResponse.ok || !tokenResult?.token) {
          console.error('❌ Failed to get Stebby token:', tokenResult);
          clearStebbySession();
          setErrorDialogMessage(GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
          setStepIndex(-1);
          return;
        }

        const registrationInput = JSON.parse(registrationInputRaw);

        const registrationWithStebbyToken = {
          ...registrationInput,
          PaymentType: 'Stebby',
          StebbyToken: tokenResult.token,
          StebbyReference: reference,
        };

        clearStebbySession();

        await submitRegistration(registrationWithStebbyToken);
      } catch (err) {
        console.error('❌ Failed to complete Stebby flow:', err);
        clearStebbySession();
        setErrorDialogMessage(GENERIC_ERROR_MSG);
        setErrorDialogOpen(true);
        setStepIndex(-1);
      }
    };

    completeStebbyFlow();
  }, [isStebbySuccessRoute, getStebby, stebbyResponse, submitRegistration]);

  useEffect(() => {
    const isPaymentCallbackRoute =
      isSuccessRoute ||
      isErrorRoute ||
      isCancelRoute ||
      isStebbySuccessRoute ||
      isStebbyCancelRoute;

    if (service == null && leftToPaysera && !isPaymentCallbackRoute) {
      if (cartState.registrationId) {
        history.push('/payment/status/cancel');
      } else {
        history.push('/');
      }
    }
  }, [
    service,
    isSuccessRoute,
    isErrorRoute,
    isCancelRoute,
    isStebbySuccessRoute,
    isStebbyCancelRoute,
    leftToPaysera,
    history,
    cartState?.registrationId,
  ]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [stepIndex]);

  const steps = useMemo(
    () => [
      {
        title: stepTitles[0],
        content: <PatientInfoStep />,
      },
      {
        title: stepTitles[1],
        content: <ServiceInfoStep />,
      },
      {
        title: stepTitles[2],
        content: (
          <PaymentStep
            onFinalize={handleFinalizeCheckout}
            isSubmitting={isFinalizing || isStebbyLoading}
            submitError={registrationError?.message}
          />
        ),
      },
    ],
    [
      stepTitles,
      handleFinalizeCheckout,
      isFinalizing,
      isStebbyLoading,
      registrationError,
    ],
  );

  useEffect(() => {
    const unblock = history.block(() => {
      const nextPath = window.location.pathname;

      if (
        nextPath.startsWith('/services') &&
        stepIndex > 0 &&
        !isSuccessRoute &&
        !isStebbySuccessRoute
      ) {
        setStepIndex((prev) => prev - 1);
        return false;
      }

      return true;
    });

    return () => unblock();
  }, [history, stepIndex, isSuccessRoute, isStebbySuccessRoute]);

  const isCompleted = stepIndex === steps.length;
  const isError = stepIndex === -1;

  const currentStepEl =
    !isCompleted && !isError && steps[stepIndex]?.content
      ? React.cloneElement(steps[stepIndex].content, {
          stepIndex,
          stepNumber: stepIndex + 1,
          totalSteps: steps.length,
          isActive: true,
          isCompleted,
          goNext: nextStep,
          goPrev: prevStep,
          setStepIndex,
        })
      : null;

  const isPaymentCallbackRoute =
    isSuccessRoute ||
    isErrorRoute ||
    isCancelRoute ||
    isStebbySuccessRoute ||
    isStebbyCancelRoute;

  if (loading && !service && !isPaymentCallbackRoute) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg font-medium">Kraunama...</div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1440px] mx-auto h-full p-4 font-primary">
      <div className="mb-4">
        <Stepper
          steps={stepTitles}
          currentStep={
            isCompleted || isSuccessRoute || isErrorRoute
              ? steps.length + 1
              : stepIndex + 1
          }
        />
      </div>

      <div className="pb-4 min-h-40">
        {isStebbySuccessRoute ? (
          <div className="flex items-center justify-center min-h-40">
            <div className="text-lg font-medium">
              Tvirtinamas Stebby mokėjimas...
            </div>
          </div>
        ) : isCompleted || isSuccessRoute ? (
          <SuccessStep />
        ) : isErrorRoute || isCancelRoute || isStebbyCancelRoute ? (
          <ErrorStep />
        ) : (
          currentStepEl
        )}
      </div>

      <RegistrationErrorDialog
        open={errorDialogOpen}
        onClose={() => {
          setErrorDialogOpen(false);
          history.push('/');
        }}
        message={errorDialogMessage}
      />
    </div>
  );
};

export default CartPage;
